import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ArrowDown, MapPin, Home, Ruler } from "lucide-react";
import { ACCORDO, TIPOLOGIE, ARREDO, APE, DURATA } from "@/data/accordo_territoriale";

const fmt = (n) => n.toLocaleString("it-IT", { maximumFractionDigits: 0 });

const selectCls =
  "w-full bg-white/[0.06] border border-white/15 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-azure transition-colors appearance-none cursor-pointer [&>option]:text-ink";

// Simulatore canone concordato — dati ufficiali Accordo territoriale 03/07/2025.
// Stima sul MASSIMALE: tariffa €/mq della microzona x superficie x fattore maggiorazioni
// PROGRESSIVO (1+ape)(1+arredo)(1+durata), come da prassi di asseverazione.
// Il calcolo esatto (superficie convenzionale, pertinenze, asseverazione) resta in consulenza.
export const CanoneCalculator = () => {
  const [comuneId, setComuneId] = useState("firenze");
  const [zonaId, setZonaId] = useState(ACCORDO.firenze.zone[0].id);
  const [tipologia, setTipologia] = useState("B");
  const [mq, setMq] = useState(80);
  const [arredo, setArredo] = useState("no");
  const [ape, setApe] = useState("gf");
  const [durata, setDurata] = useState("3+2");

  const comune = ACCORDO[comuneId];
  const zona = comune.zone.find((z) => z.id === zonaId) || comune.zone[0];
  const tariffa = zona[tipologia];
  // Le maggiorazioni dell'accordo sono PROGRESSIVE (si compongono moltiplicando), non a sommatoria
  const fattori = [
    APE.find((a) => a.id === ape)?.pct || 0,
    ARREDO.find((a) => a.id === arredo)?.pct || 0,
    DURATA.find((d) => d.id === durata)?.pct || 0,
  ];
  const fattoreMagg = fattori.reduce((acc, p) => acc * (1 + p), 1);

  const canoneMax = Math.round(mq * tariffa * fattoreMagg);
  const annuo = canoneMax * 12;
  const risparmioAnno = Math.round(annuo * 0.21 - annuo * 0.10);
  // Stesso netto: netto concordato (10%) = netto libero (21%) -> L = C * 0.90 / 0.79
  const nettoMese = Math.round(canoneMax * 0.9);
  const liberoEquivalente = Math.round((canoneMax * 0.9) / 0.79);
  const differenzaInquilino = liberoEquivalente - canoneMax;

  const onComune = (id) => {
    setComuneId(id);
    setZonaId(ACCORDO[id].zone[0].id);
  };

  return (
    <section id="canone-calculator" className="bg-ink text-white" data-testid="canone-calculator">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-12"
        >
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-azure mb-4">
            Simulatore · Accordo territoriale 3 luglio 2025
          </p>
          <h2 className="font-serif font-bold text-3xl sm:text-4xl leading-tight mb-4">
            Quanto vale il tuo canone concordato?
          </h2>
          <p className="text-white/60 text-sm sm:text-base leading-relaxed">
            Scegli comune, zona e tipologia: la stima usa i massimali ufficiali dell'accordo territoriale.
            Poi guarda quanto risparmi di tasse rispetto al canone libero.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* INPUT */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="space-y-5"
          >
            <div>
              <label htmlFor="sim-comune" className="flex items-center gap-2 text-sm text-white/70 mb-2">
                <MapPin size={14} className="text-azure" /> Comune
              </label>
              <select id="sim-comune" data-testid="sim-comune-select" className={selectCls} value={comuneId} onChange={(e) => onComune(e.target.value)}>
                {Object.entries(ACCORDO).map(([id, c]) => (
                  <option key={id} value={id}>{c.nome}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="sim-zona" className="flex items-center gap-2 text-sm text-white/70 mb-2">
                <MapPin size={14} className="text-azure" /> Zona
              </label>
              <select id="sim-zona" data-testid="sim-zona-select" className={selectCls} value={zona.id} onChange={(e) => setZonaId(e.target.value)}>
                {comune.zone.map((z) => (
                  <option key={z.id} value={z.id}>{z.nome}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="sim-tipologia" className="flex items-center gap-2 text-sm text-white/70 mb-2">
                <Home size={14} className="text-azure" /> Tipologia di immobile
              </label>
              <select id="sim-tipologia" data-testid="sim-tipologia-select" className={selectCls} value={tipologia} onChange={(e) => setTipologia(e.target.value)}>
                {TIPOLOGIE.map((t) => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
              <p className="text-xs text-white/40 mt-1.5">{TIPOLOGIE.find((t) => t.id === tipologia)?.descr}</p>
            </div>

            <div>
              <label htmlFor="sim-mq" className="flex items-center gap-2 text-sm text-white/70 mb-2">
                <Ruler size={14} className="text-azure" /> Superficie abitabile:{" "}
                <span className="font-semibold text-gold" data-testid="sim-mq-value">{mq} mq</span>
              </label>
              <input
                id="sim-mq"
                data-testid="sim-mq-slider"
                type="range"
                min="30"
                max="200"
                step="5"
                value={mq}
                onChange={(e) => setMq(Number(e.target.value))}
                className="w-full accent-gold cursor-pointer"
              />
              <div className="flex justify-between text-xs text-white/40 mt-1">
                <span>30 mq</span>
                <span>200 mq</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="sim-ape" className="block text-xs text-white/70 mb-2">Classe energetica</label>
                <select id="sim-ape" data-testid="sim-ape-select" className={selectCls} value={ape} onChange={(e) => setApe(e.target.value)}>
                  {APE.map((a) => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sim-arredo" className="block text-xs text-white/70 mb-2">Arredo</label>
                <select id="sim-arredo" data-testid="sim-arredo-select" className={selectCls} value={arredo} onChange={(e) => setArredo(e.target.value)}>
                  {ARREDO.map((a) => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="sim-durata" className="block text-xs text-white/70 mb-2">Durata</label>
                <select id="sim-durata" data-testid="sim-durata-select" className={selectCls} value={durata} onChange={(e) => setDurata(e.target.value)}>
                  {DURATA.map((d) => (
                    <option key={d.id} value={d.id}>{d.nome}</option>
                  ))}
                </select>
              </div>
            </div>
          </motion.div>

          {/* RISULTATO */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="bg-white/[0.05] border border-white/12 rounded-3xl p-7 sm:p-9 lg:sticky lg:top-24"
          >
            <div className="space-y-3.5 text-sm">
              <div className="flex justify-between items-center border-b border-white/10 pb-3.5">
                <span className="text-white/60 flex items-center gap-2">
                  <Calculator size={15} className="text-azure" /> Tariffa massimale {comune.nome}, zona selezionata
                </span>
                <span className="font-semibold" data-testid="sim-tariffa">{tariffa.toFixed(2).replace(".", ",")} €/mq</span>
              </div>
              {fattoreMagg > 1 && (
                <div className="flex justify-between items-center border-b border-white/10 pb-3.5">
                  <span className="text-white/60">Tariffa maggiorata</span>
                  <span className="font-semibold text-azure" data-testid="sim-tariffa-maggiorata">
                    {(tariffa * fattoreMagg).toFixed(2).replace(".", ",")} €/mq
                  </span>
                </div>
              )}
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs uppercase tracking-[0.18em] text-white/50 mb-1">Canone concordato massimo stimato</p>
              <p className="font-serif font-bold text-5xl sm:text-6xl text-azure" data-testid="sim-canone-max">{fmt(canoneMax)} €<span className="text-xl text-white/50 font-sans font-normal">/mese</span></p>
              <p className="text-white/50 text-xs mt-2">netto con cedolare al 10%: <span className="text-white font-semibold" data-testid="sim-netto-mese">{fmt(nettoMese)} €/mese</span></p>
            </div>

            <div className="mt-5 bg-emerald-500/10 border border-emerald-400/30 rounded-2xl p-5 text-center">
              <p className="text-xs uppercase tracking-[0.18em] text-emerald-300/80 mb-1">Risparmio simulato sulle tasse, a parità di canone (10% vs 21%)</p>
              <p className="font-serif font-bold text-4xl text-emerald-400" data-testid="sim-risparmio">{fmt(risparmioAnno)} €<span className="text-lg text-emerald-300/70 font-sans font-normal">/anno</span></p>
              <p className="text-emerald-300/60 text-xs mt-1.5" data-testid="sim-risparmio-5anni">
                su un contratto 3+2 sarebbero <span className="font-semibold text-emerald-300">{fmt(risparmioAnno * 5)} €</span> + riduzione IMU
              </p>
            </div>

            <div className="mt-5 bg-red-500/10 border border-red-400/30 rounded-2xl p-5">
              <p className="text-sm text-white/70 leading-relaxed text-center">
                Per lo <span className="text-white font-semibold">stesso netto</span> col canone libero (tassato al 21%)
                dovresti chiedere <span className="text-white font-semibold" data-testid="sim-libero-equivalente">{fmt(liberoEquivalente)} €/mese</span>
              </p>
              <p className="text-xs text-red-200/70 text-center mt-2 leading-relaxed" data-testid="sim-differenza-inquilino">
                L'inquilino pagherebbe <span className="text-red-300 font-semibold">{fmt(differenzaInquilino)} € in più ogni mese</span>,
                interamente assorbiti dalla maggiore tassazione: il tuo netto resterebbe identico
              </p>
            </div>

            {mq < 63 && (
              <div className="mt-5 bg-amber-400/[0.08] border border-amber-300/30 rounded-2xl p-4 text-center" data-testid="sim-note-piccoli">
                <p className="text-xs text-amber-200/90 leading-relaxed">
                  Immobili fino a 63 mq: l'accordo prevede <span className="font-semibold">ulteriori maggiorazioni da determinare</span> caso
                  per caso sulla superficie convenzionale — il canone reale può essere più alto. Richiedi il calcolo esatto qui sotto.
                </p>
              </div>
            )}

            <button
              onClick={() => document.getElementById("richiedi")?.scrollIntoView({ behavior: "smooth" })}
              data-testid="calculator-cta-btn"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold/90 text-ink font-semibold rounded-full px-7 py-4 text-sm transition-colors"
            >
              Calcolo esatto con asseverazione inclusa <ArrowDown size={16} />
            </button>
            <div className="mt-6 bg-amber-400/[0.08] border border-amber-300/30 rounded-2xl p-5 flex items-center gap-4" data-testid="sim-disclaimer">
              <img
                src="/assets/takes-care-logo.jpg"
                alt="Takes Care — Semplificaffitto"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full shrink-0 border-2 border-amber-300/40 shadow-lg animate-[spin_18s_linear_infinite]"
                data-testid="sim-disclaimer-stamp"
              />
              <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                Questa è una <span className="text-white font-medium">simulazione indicativa</span> basata sui massimali
                dell'accordo territoriale. Per una valutazione accurata servono i dati reali e la contestualizzazione
                dell'immobile — superficie convenzionale, pertinenze, stato e dotazioni. Il calcolo esatto è{" "}
                <span className="text-white font-medium">gratuito per chi aderisce al protocollo Semplificaffitto.it</span>.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
